import React, { useCallback, useMemo, useState } from 'react';
import Body from '@plone/volto/components/manage/Blocks/Video/Body';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Image from '@plone/volto/components/theme/Image/Image';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Input, Message } from 'semantic-ui-react';

import { getVideoFileSource } from './utils';
import VideoContentSidebar from './VideoSidebar';

import videoBlockSVG from '@plone/volto/components/manage/Blocks/Video/block-video.svg';
import aheadSVG from '@plone/volto/icons/ahead.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

const messages = defineMessages({
  VideoFormDescription: {
    id: 'Specify a youtube video, playlist URL or upload an MP4 file in the content form',
    defaultMessage:
      'Specify a youtube video, playlist URL or upload an MP4 file in the content form',
  },
  VideoBlockInputPlaceholder: {
    id: 'Type a Video (YouTube, Vimeo or mp4) URL',
    defaultMessage: 'Type a Video (YouTube, Vimeo or mp4) URL',
  },
  VideoTagFallback: {
    id: 'Your browser does not support HTML5 video playback.',
    defaultMessage: 'Your browser does not support HTML5 video playback.',
  },
});

const Edit = (props) => {
  const { data, selected, properties, onChangeField } = props;
  const intl = useIntl();
  const [url, setUrl] = useState('');

  // Lê a URL do campo do conteúdo (única fonte de verdade)
  const contentUrl = properties?.url || '';
  const contentFile = properties?.file;

  const onChangeUrl = ({ target }) => {
    setUrl(target.value);
  };

  const onSubmitUrl = useCallback(() => {
    if (!url) return;

    // Atualiza apenas o campo do conteúdo
    if (onChangeField) {
      onChangeField('url', url);
    }

    setUrl('');
  }, [url, onChangeField]);

  const resetSubmitUrl = useCallback(() => {
    setUrl('');
    if (onChangeField) {
      onChangeField('url', '');
    }
  }, [onChangeField]);

  const onKeyDownVariantMenuForm = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onSubmitUrl();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        resetSubmitUrl();
      }
    },
    [onSubmitUrl, resetSubmitUrl],
  );

  const placeholder = useMemo(
    () =>
      data.placeholder ||
      intl.formatMessage(messages.VideoBlockInputPlaceholder),
    [intl, data],
  );

  const uploadedFileSrc = useMemo(() => {
    let fileSource = getVideoFileSource(contentFile);
    if (!fileSource) return null;
    return fileSource.startsWith('data:video/mp4')
      ? fileSource
      : `${fileSource}/${contentFile.filename}`;
  }, [contentFile]);

  const hasUploadedFile = Boolean(uploadedFileSrc);

  return (
    <div
      className={cx(
        'block video align videocontent',
        {
          selected: selected,
          center: !Boolean(data.align),
        },
        data.align,
      )}
    >
      {contentUrl ? (
        <Body data={{ ...data, url: contentUrl }} isEditMode={true} />
      ) : hasUploadedFile ? (
        <Body
          data={{
            ...data,
            url: uploadedFileSrc,
            preview_image: data.preview_image,
          }}
          isEditMode={true}
        />
      ) : (
        <Message>
          <center>
            <Image src={videoBlockSVG} alt="" />
            <p>{intl.formatMessage(messages.VideoFormDescription)}</p>
            <div className="toolbar-inner">
              <Input
                onKeyDown={onKeyDownVariantMenuForm}
                onChange={onChangeUrl}
                placeholder={placeholder}
                value={url}
                onClick={(e) => e.stopPropagation()}
              />
              {url && (
                <Button.Group>
                  <Button
                    type="button"
                    basic
                    icon
                    onClick={(e) => {
                      e.stopPropagation();
                      resetSubmitUrl();
                    }}
                  >
                    <Icon name={clearSVG} size="24px" />
                  </Button>
                </Button.Group>
              )}
              <Button.Group>
                <Button
                  type="button"
                  basic
                  primary
                  icon
                  disabled={!url}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSubmitUrl();
                  }}
                >
                  <Icon name={aheadSVG} size="24px" />
                </Button>
              </Button.Group>
            </div>
          </center>
        </Message>
      )}
      <SidebarPortal selected={selected}>
        <VideoContentSidebar
          {...props}
          data={{ ...data, url: contentUrl, file: contentFile }}
          onClearUrl={() => {
            if (onChangeField) {
              onChangeField('url', '');
            }
          }}
        />
      </SidebarPortal>
    </div>
  );
};

Edit.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  block: PropTypes.string.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  onChangeField: PropTypes.func,
  selected: PropTypes.bool.isRequired,
  properties: PropTypes.objectOf(PropTypes.any),
};

export default withBlockExtensions(Edit);
